import { useState, useEffect } from 'react';
import { Truck, Plus, Search, DollarSign, Calendar, TrendingUp, Edit2, Trash2, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useToast } from '../../contexts/ToastContext';

interface Truck {
  id: string;
  unit_number: string;
  make: string;
  model: string;
  year: number;
  vin: string;
}

interface NewTruckPurchase {
  id: string;
  truck_id: string;
  truck_unit_number: string;
  truck_make: string;
  truck_model: string;
  truck_year: number;
  total_amount: number;
  payment_frequency: 'weekly' | 'bi-weekly' | 'monthly';
  payment_week?: number; // 1-4 for monthly, 1-2 for bi-weekly
  payment_day?: number; // Day of week (1-7) or day of month (1-31)
  payment_amount: number;
  start_date: string;
  end_date?: string;
  remaining_balance: number;
  status: 'active' | 'paid_off' | 'cancelled';
  notes?: string;
  created_at: string;
  updated_at: string;
}

export function NewTrucksView() {
  const toast = useToast();
  const [purchases, setPurchases] = useState<NewTruckPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Truck[]>([]);
  const [selectedTruck, setSelectedTruck] = useState<Truck | null>(null);
  const [editingPurchase, setEditingPurchase] = useState<NewTruckPurchase | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    total_amount: '',
    payment_amount: '',
    payment_frequency: 'monthly' as 'weekly' | 'bi-weekly' | 'monthly',
    payment_week: 1,
    payment_day: 1,
    start_date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('new_truck_purchases')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPurchases(data || []);
    } catch (error: any) {
      console.error('Error fetching purchases:', error);
      toast.error('Failed to load truck purchases');
    } finally {
      setLoading(false);
    }
  };

  const searchTrucks = async (query: string) => {
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('trucks')
        .select('id, unit_number, make, model, year, vin')
        .or(`unit_number.ilike.%${query}%,make.ilike.%${query}%,model.ilike.%${query}%,vin.ilike.%${query}%`)
        .limit(10);

      if (error) throw error;
      setSearchResults(data || []);
    } catch (error: any) {
      console.error('Error searching trucks:', error);
    }
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTruck) {
      toast.error('Please select a truck');
      return;
    }

    const totalAmount = parseFloat(formData.total_amount);
    if (isNaN(totalAmount) || totalAmount <= 0) {
      toast.error('Please enter a valid total amount');
      return;
    }

    const paymentAmount = parseFloat(formData.payment_amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      const purchaseData = {
        truck_id: selectedTruck.id,
        truck_unit_number: selectedTruck.unit_number,
        truck_make: selectedTruck.make,
        truck_model: selectedTruck.model,
        truck_year: selectedTruck.year,
        total_amount: totalAmount,
        payment_frequency: formData.payment_frequency,
        payment_week: formData.payment_week,
        payment_day: formData.payment_day,
        payment_amount: paymentAmount,
        start_date: formData.start_date,
        remaining_balance: totalAmount,
        status: 'active',
        notes: formData.notes,
      };

      if (editingPurchase) {
        const { error } = await supabase
          .from('new_truck_purchases')
          .update(purchaseData)
          .eq('id', editingPurchase.id);

        if (error) throw error;
        toast.success('Truck purchase updated successfully');
      } else {
        const { error } = await supabase
          .from('new_truck_purchases')
          .insert([purchaseData]);

        if (error) throw error;
        toast.success('Truck purchase added successfully');
      }

      fetchPurchases();
      handleCloseModal();
    } catch (error: any) {
      console.error('Error saving purchase:', error);
      toast.error('Failed to save truck purchase');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this truck purchase?')) return;

    try {
      const { error } = await supabase
        .from('new_truck_purchases')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Truck purchase deleted successfully');
      fetchPurchases();
    } catch (error: any) {
      console.error('Error deleting purchase:', error);
      toast.error('Failed to delete truck purchase');
    }
  };

  const handleEdit = (purchase: NewTruckPurchase) => {
    setEditingPurchase(purchase);
    setSelectedTruck({
      id: purchase.truck_id,
      unit_number: purchase.truck_unit_number,
      make: purchase.truck_make,
      model: purchase.truck_model,
      year: purchase.truck_year,
      vin: '',
    });
    setFormData({
      total_amount: purchase.total_amount.toString(),
      payment_amount: purchase.payment_amount.toString(),
      payment_frequency: purchase.payment_frequency,
      payment_week: purchase.payment_week || 1,
      payment_day: purchase.payment_day || 1,
      start_date: purchase.start_date,
      notes: purchase.notes || '',
    });
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingPurchase(null);
    setSelectedTruck(null);
    setSearchQuery('');
    setSearchResults([]);
    setFormData({
      total_amount: '',
      payment_amount: '',
      payment_frequency: 'monthly',
      payment_week: 1,
      payment_day: 1,
      start_date: new Date().toISOString().split('T')[0],
      notes: '',
    });
  };

  const getTotalStats = () => {
    const activePurchases = purchases.filter(p => p.status === 'active');
    const totalInvestment = activePurchases.reduce((sum, p) => sum + p.total_amount, 0);
    const totalRemaining = activePurchases.reduce((sum, p) => sum + p.remaining_balance, 0);
    const totalPaid = totalInvestment - totalRemaining;
    const monthlyPayments = activePurchases
      .filter(p => p.payment_frequency === 'monthly')
      .reduce((sum, p) => sum + p.payment_amount, 0);

    return { totalInvestment, totalRemaining, totalPaid, monthlyPayments, count: activePurchases.length };
  };

  const stats = getTotalStats();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900 via-blue-900 to-indigo-900 rounded-xl shadow-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">New Truck Purchases</h2>
            <p className="text-blue-100">Track and manage new truck acquisitions and payment schedules</p>
          </div>
          <div className="bg-white bg-opacity-20 p-4 rounded-lg">
            <Truck className="w-12 h-12" />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Trucks</p>
              <p className="text-2xl font-bold text-gray-900">{stats.count}</p>
            </div>
            <Truck className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Investment</p>
              <p className="text-2xl font-bold text-gray-900">${stats.totalInvestment.toLocaleString()}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Remaining Balance</p>
              <p className="text-2xl font-bold text-gray-900">${stats.totalRemaining.toLocaleString()}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-orange-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Monthly Payments</p>
              <p className="text-2xl font-bold text-gray-900">${stats.monthlyPayments.toLocaleString()}</p>
            </div>
            <Calendar className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Add Button */}
      <div className="flex justify-end">
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          Add New Truck Purchase
        </button>
      </div>

      {/* Purchases List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Truck
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Frequency
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Remaining Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : purchases.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                    No truck purchases found. Add your first truck purchase to get started.
                  </td>
                </tr>
              ) : (
                purchases.map((purchase) => (
                  <tr key={purchase.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {purchase.truck_unit_number}
                      </div>
                      <div className="text-sm text-gray-500">
                        {purchase.truck_year} {purchase.truck_make} {purchase.truck_model}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${purchase.total_amount.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 capitalize">
                        {purchase.payment_frequency.replace('-', ' ')}
                      </span>
                      {purchase.payment_week && (
                        <div className="text-xs text-gray-500">Week {purchase.payment_week}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${purchase.payment_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${purchase.remaining_balance.toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        purchase.status === 'active' ? 'bg-green-100 text-green-800' :
                        purchase.status === 'paid_off' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {purchase.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEdit(purchase)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(purchase.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">
                  {editingPurchase ? 'Edit Truck Purchase' : 'Add New Truck Purchase'}
                </h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Truck Search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search Truck
                  </label>
                  {selectedTruck ? (
                    <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">{selectedTruck.unit_number}</div>
                        <div className="text-sm text-gray-600">
                          {selectedTruck.year} {selectedTruck.make} {selectedTruck.model}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedTruck(null)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          searchTrucks(e.target.value);
                        }}
                        placeholder="Search by unit number, make, model, or VIN..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      {searchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                          {searchResults.map((truck) => (
                            <button
                              key={truck.id}
                              type="button"
                              onClick={() => {
                                setSelectedTruck(truck);
                                setSearchQuery('');
                                setSearchResults([]);
                              }}
                              className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">{truck.unit_number}</div>
                              <div className="text-sm text-gray-600">
                                {truck.year} {truck.make} {truck.model}
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Total Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Total Amount
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="number"
                      value={formData.total_amount}
                      onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Payment Frequency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Frequency
                  </label>
                  <select
                    value={formData.payment_frequency}
                    onChange={(e) => setFormData({ ...formData, payment_frequency: e.target.value as any })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="weekly">Weekly</option>
                    <option value="bi-weekly">Bi-Weekly (Every 2 weeks)</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>

                {/* Payment Amount */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Amount (per payment)
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="number"
                      value={formData.payment_amount}
                      onChange={(e) => setFormData({ ...formData, payment_amount: e.target.value })}
                      placeholder="0.00"
                      step="0.01"
                      required
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Enter the amount you will pay each {formData.payment_frequency === 'weekly' ? 'week' : formData.payment_frequency === 'bi-weekly' ? 'two weeks' : 'month'}
                  </p>
                </div>

                {/* Payment Week */}
                {(formData.payment_frequency === 'monthly' || formData.payment_frequency === 'bi-weekly') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {formData.payment_frequency === 'monthly' ? 'Week of Month' : 'Week of Bi-Weekly Period'}
                    </label>
                    <select
                      value={formData.payment_week}
                      onChange={(e) => setFormData({ ...formData, payment_week: parseInt(e.target.value) })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={1}>Week 1</option>
                      <option value={2}>Week 2</option>
                      {formData.payment_frequency === 'monthly' && (
                        <>
                          <option value={3}>Week 3</option>
                          <option value={4}>Week 4</option>
                        </>
                      )}
                    </select>
                  </div>
                )}

                {/* Payment Day */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {formData.payment_frequency === 'weekly' || formData.payment_frequency === 'bi-weekly' 
                      ? 'Day of Week' 
                      : 'Day of Month'}
                  </label>
                  <select
                    value={formData.payment_day}
                    onChange={(e) => setFormData({ ...formData, payment_day: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {formData.payment_frequency === 'weekly' || formData.payment_frequency === 'bi-weekly' ? (
                      <>
                        <option value={1}>Monday</option>
                        <option value={2}>Tuesday</option>
                        <option value={3}>Wednesday</option>
                        <option value={4}>Thursday</option>
                        <option value={5}>Friday</option>
                        <option value={6}>Saturday</option>
                        <option value={7}>Sunday</option>
                      </>
                    ) : (
                      Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                        <option key={day} value={day}>{day}</option>
                      ))
                    )}
                  </select>
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Payment Schedule Preview */}
                {formData.total_amount && formData.payment_amount && formData.start_date && (() => {
                  const totalAmount = parseFloat(formData.total_amount);
                  const paymentAmount = parseFloat(formData.payment_amount);
                  const numberOfPayments = Math.ceil(totalAmount / paymentAmount);
                  const startDate = new Date(formData.start_date);
                  
                  const getNextPaymentDate = (index: number) => {
                    const date = new Date(startDate);
                    
                    if (formData.payment_frequency === 'weekly') {
                      date.setDate(date.getDate() + (index * 7));
                    } else if (formData.payment_frequency === 'bi-weekly') {
                      date.setDate(date.getDate() + (index * 14));
                    } else if (formData.payment_frequency === 'monthly') {
                      date.setMonth(date.getMonth() + index);
                      date.setDate(formData.payment_day);
                    }
                    
                    return date;
                  };
                  
                  const payments = Array.from({ length: Math.min(numberOfPayments, 12) }, (_, i) => ({
                    number: i + 1,
                    date: getNextPaymentDate(i),
                    amount: i === numberOfPayments - 1 ? totalAmount - (paymentAmount * (numberOfPayments - 1)) : paymentAmount,
                  }));
                  
                  return (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-900">Payment Schedule Preview</h4>
                        <span className="text-xs text-gray-600">
                          {numberOfPayments} total payments
                          {numberOfPayments > 12 && ' (showing first 12)'}
                        </span>
                      </div>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {payments.map((payment) => (
                          <div
                            key={payment.number}
                            className="flex items-center justify-between bg-white rounded-lg p-3 shadow-sm"
                          >
                            <div className="flex items-center gap-3">
                              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-xs font-bold">
                                {payment.number}
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {payment.date.toLocaleDateString('en-US', { 
                                    weekday: 'short',
                                    month: 'short', 
                                    day: 'numeric', 
                                    year: 'numeric' 
                                  })}
                                </div>
                                <div className="text-xs text-gray-500">
                                  {formData.payment_frequency === 'weekly' && 'Weekly payment'}
                                  {formData.payment_frequency === 'bi-weekly' && 'Bi-weekly payment'}
                                  {formData.payment_frequency === 'monthly' && `Week ${formData.payment_week}, Day ${formData.payment_day}`}
                                </div>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-bold text-blue-600">
                                ${payment.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              {payment.number === numberOfPayments && (
                                <div className="text-xs text-green-600 font-medium">Final payment</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-blue-200 flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-700">Estimated Payoff Date:</span>
                        <span className="text-sm font-bold text-gray-900">
                          {getNextPaymentDate(numberOfPayments - 1).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    placeholder="Add any additional notes..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingPurchase ? 'Update Purchase' : 'Add Purchase'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
